import { useState } from 'react';

import closeIcon from '@shared/assets/images/close_icon.svg';
import DateInput from '@shared/components/atoms/DateInput';
import Alert from '@shared/components/atoms/Alert';

import styles from '@shared/components/organisms/SearchFilter/SearchFilter.module.scss';

/**
 * SearchFilter 컴포넌트
 * - 결재 관련 검색/필터 공용 컴포넌트
 * - 필터 필드 정의를 props로 받아 동적으로 렌더링
 * - 피그마 디자인 기반 구현 (586-376, 586-455)
 *
 * @param {Array} fields - 필터 필드 배열
 * @param {Function} onFilterChange - 필터 값 변경 핸들러
 * @param {Function} onSearch - 검색/조회 버튼 클릭 핸들러
 * @param {Function} onReset - 초기화 버튼 클릭 핸들러
 * @param {Function} onExcel - Excel 버튼 클릭 핸들러 (선택적)
 * @param {boolean} showExcelButton - Excel 버튼 표시 여부 (기본값: false)
 * @param {string} searchButtonText - 검색 버튼 텍스트 (기본값: '검색')
 */
export default function SearchFilter({
  fields = [],
  onFilterChange,
  onSearch,
  onReset,
  onExcel,
  showExcelButton = false,
  searchButtonText = '검색',
  extraContent,
  extraContentPosition = 'below', // 'below' | 'actions' // 추가 엘리멘트문을 action 버튼과 같이둘지 분리할지의 여부
  showSearchButton = true, // 조회버튼 뷰 여부
  showResetButton = true, // 초기화버튼 뷰 여부
  empCount, // 총 직원수

  documnetCount, // 총 문서 건수
}) {
  const [alertState, setAlertState] = useState({ isOpen: false, message: '' });

  function showAlert(message) {
    setAlertState({ isOpen: true, message });
  }

  function closeAlert() {
    setAlertState({ isOpen: false, message: '' });
  }

  // 날짜 유효성 검증
  function validateDate(dateStr) {
    if (!dateStr) return true; // 빈 값은 허용
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateStr)) return false;
    const date = new Date(dateStr);
    return !isNaN(date.getTime());
  }

  // 날짜 범위 필드 검증
  function validateDateRangeFields() {
    const dateRangeFields = fields.filter((f) => f.type === 'dateRange');
    for (const field of dateRangeFields) {
      if (!validateDate(field.startValue) || !validateDate(field.endValue)) {
        showAlert('조회기간을 잘못 입력하셨습니다. 확인 후 다시 입력해 주세요. 예) 20261017');
        return false;
      }
      const start = new Date(field.startValue);
      const end = new Date(field.endValue);
      if (start.getTime() > end.getTime()) {
        showAlert('날짜를 다시 확인해 주세요.');
        return false;
      }
    }
    return true;
  }

  function handleInputChange(fieldKey, value) {
    if (onFilterChange) {
      onFilterChange(fieldKey, value);
    }
  }

  function handleSearch() {
    // 날짜 검증
    if (!validateDateRangeFields()) {
      return;
    }

    if (onSearch) {
      onSearch();
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  }

  function handleExcel() {
    if (onExcel) {
      onExcel();
    }
  }

  // 필드들을 행별로 그룹화
  function groupFieldsByRow(fieldsArray) {
    const rows = [];
    let currentRow = [];

    fieldsArray.forEach(function (field) {
      if (field.newRow && currentRow.length > 0) {
        rows.push(currentRow);
        currentRow = [];
      }
      currentRow.push(field); // newRow 여부와 관계없이 항상 추가
    });

    if (currentRow.length > 0) {
      rows.push(currentRow);
    }

    return rows;
  }

  const fieldRows = groupFieldsByRow(fields);

  // 필터 그룹 클래스 결정
  function getFilterGroupClass(field) {
    if (field.fill) {
      return `${styles.filterGroup} ${styles.filterGroupFill}`;
    }
    if (field.wide) {
      return `${styles.filterGroup} ${styles.filterGroupWide}`;
    }
    return styles.filterGroup;
  }

  // 문자열 → Date 객체 변환
  function toDate(str) {
    if (!str) return null;
    const d = new Date(str);
    return isNaN(d.getTime()) ? null : d;
  }

  // Date 객체 → YYYY-MM-DD 문자열 변환
  function formatDate(date) {
    if (!date) return '';
    try {
      return date.toISOString().slice(0, 10);
    } catch {
      return '';
    }
  }

  // 날짜 범위 필드 렌더링
  function renderDateRangeField(field) {
    const startDate = toDate(field.startValue);
    const endDate = toDate(field.endValue);

    return (
      <div key={field.key} className={getFilterGroupClass(field)}>
        <div className={styles.labelBox}>{field.label}</div>

        <div className={styles.dateInputGroup}>
          {/* 시작일 */}
          <div className={styles.dateInputWrapper}>
            <DateInput
              id={field.startKey}
              value={field.startValue || ''}
              onChange={(value) => handleInputChange(field.startKey, value)}
              placeholder={field.startPlaceholder || '2025-02-17'}
              maxDate={endDate}
            />
          </div>

          <span className={styles.dateSeparator}>~</span>

          {/* 종료일 */}
          <div className={styles.dateInputWrapper}>
            <DateInput
              id={field.endKey}
              value={field.endValue || ''}
              onChange={(value) => handleInputChange(field.endKey, value)}
              placeholder={field.endPlaceholder || '2025-02-17'}
              minDate={startDate}
            />
          </div>
        </div>

        {field.key === 'queryPeriod' && (
          <span className={styles.documentCount}> {documnetCount}건</span>
        )}
      </div>
    );
  }

  // 선택 필드 렌더링
  function renderSelectField(field) {
    return (
      <div key={field.key} className={getFilterGroupClass(field)}>
        <div className={styles.labelBox}>{field.label}</div>
        <div className={styles.selectWrapper}>
          <select
            id={field.key}
            className={styles.select}
            value={field.value ?? ''}
            onChange={function (e) {
              handleInputChange(field.key, e.target.value);
            }}
            aria-label={field.label}
          >
            {field.options?.map(function (option) {
              if (!option) return null;
              if (typeof option === 'string') {
                return (
                  <option key={option} value={option}>
                    {option}
                  </option>
                );
              }
              return (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              );
            })}
          </select>
        </div>
        {field.key === 'parentDepartment' && <span className={styles.empCount}>{empCount}명</span>}
      </div>
    );
  }

  // 입력 필드 렌더링
  function renderInputField(field) {
    // 최대 글자수 (기본값 200)
    const maxLength = field.maxLength || 200;

    return (
      <div key={field.key} className={getFilterGroupClass(field)}>
        <div className={styles.labelBox}>{field.label}</div>
        <div className={styles.inputContainer}>
          <input
            id={field.key}
            type="text"
            className={`${styles.input} ${field.wide ? styles.inputWide : ''}`}
            placeholder={field.placeholder || ''}
            value={field.value || ''}
            maxLength={maxLength}
            onChange={function (e) {
              handleInputChange(field.key, e.target.value);
            }}
            aria-label={field.label}
          />
          {field.value && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={() => handleInputChange(field.key, '')}
              title="초기화"
            >
              <img src={closeIcon} alt="초기화" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // 필드 타입에 따라 렌더링
  function renderField(field) {
    if (field.type === 'dateRange') {
      return renderDateRangeField(field);
    }

    if (field.type === 'select') {
      return renderSelectField(field);
    }

    if (field.type === 'input') {
      return renderInputField(field);
    }

    return null;
  }

  return (
    <div className={styles.searchFilterContainer} onKeyDown={handleKeyDown}>
      <div className={styles.filterSection}>
        <div className={styles.scrollInner}>
          {/* 상단 테두리 */}
          <div className={styles.filterTopBorder} />
          {fieldRows.map(function (row, rowIndex) {
            const rowKey =
              row.find(function (field) {
                return field.key;
              })?.key || `row-${rowIndex}`;

            return (
              <div key={rowKey}>
                <div className={styles.filterRow}>
                  <div className={styles.filterRowContent}>
                    {row.map(function (field) {
                      return renderField(field);
                    })}
                  </div>
                </div>
                {/* 각 행 사이에 구분선 추가 */}
                <div className={styles.filterDivider} />
              </div>
            );
          })}
        </div>
      </div>
      {extraContent && extraContentPosition === 'below' && (
        <div className={styles.extraContentWrapper}>{extraContent}</div>
      )}
      {/* 액션 버튼 영역 */}
      <div className={styles.filterActions}>
        {showExcelButton && (
          <button type="button" className={styles.excelButton} onClick={handleExcel}>
            <span className={styles.excelIcon} />
            <span>EXCEL</span>
          </button>
        )}
        {showResetButton && (
          <button type="button" className={styles.resetButton} onClick={onReset}>
            <span className={styles.refreshIcon} />
            <span>초기화</span>
          </button>
        )}
        {showSearchButton && (
          <button type="button" className={styles.queryButton} onClick={handleSearch}>
            <span className={styles.searchIcon} />
            <span>{searchButtonText}</span>
          </button>
        )}

        {/* actions 라인에 포함 */}
        {extraContent && extraContentPosition === 'actions' && (
          <div className={styles.extraContentInline}>{extraContent}</div>
        )}
      </div>

      {/* Alert 컴포넌트 */}
      <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
    </div>
  );
}
